import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Stethoscope, ShieldCheck, Activity, FlaskConical, BedDouble, ReceiptText,
  ArrowRight, Globe,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { InstallButton, InstallPill } from "@/components/PWAInstall";

const PLAY_URL = "https://play.google.com/store/apps/details?id=com.fhapas.app";
import { JURISDICTIONS } from "@/lib/format";

const features = [
  { icon: Stethoscope, title: "Clinical & EMR", desc: "Encounters, diagnoses, care plans and e-prescriptions in one record." },
  { icon: FlaskConical, title: "Labs & Radiology", desc: "Order tests, track specimens and report results with full turnaround visibility." },
  { icon: BedDouble, title: "Inpatient & Theatre", desc: "Ward and bed management, admissions, discharges and OR scheduling." },
  { icon: ReceiptText, title: "Billing & Claims", desc: "Invoices, payment plans and insurance claims with multi-currency support." },
  { icon: Activity, title: "Analytics", desc: "Live operational KPIs, revenue trends and workforce insight." },
  { icon: ShieldCheck, title: "Roles & Audit", desc: "Granular RBAC, staff invitations and a complete audit trail." },
];

export default function Landing() {
  const navigate = useNavigate();
  useEffect(() => {
    // Auto-detect: returning users from a branded org land on their branded login.
    const bp = localStorage.getItem("fhapas_brand_path");
    if (bp) navigate(`/${bp}`, { replace: true });
  }, [navigate]);
  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-[var(--ticker-h,0px)] z-30 border-b border-slate-100 glass">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
          <div className="flex shrink-0 items-center gap-2.5">
            <img src="/icon-192.png" alt="FHAPAS" className="h-9 w-9 rounded-lg" />
            <span className="font-display text-lg font-extrabold tracking-tight text-slate-900">FHAPAS</span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <InstallButton className="hidden sm:inline-flex" />
            <Link to="/login"><Button variant="ghost" data-testid="landing-login" className="text-slate-700">Sign in</Button></Link>
            <Link to="/signup"><Button data-testid="landing-signup" className="bg-teal-700 hover:bg-teal-800">Get started</Button></Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 py-14 sm:px-6 lg:grid-cols-2 lg:py-24">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <InstallPill />
            <h1 className="mt-5 font-display text-4xl font-extrabold leading-none tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              The complete hospital & patient administration system
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
              Fendana Hospital and Patient Administration System (FHAPAS) unifies clinical, diagnostics, pharmacy,
              inpatient, billing and analytics for hospitals, labs and imaging centres.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup"><Button size="lg" data-testid="hero-register-org" className="gap-2 bg-teal-700 hover:bg-teal-800">Register your organisation <ArrowRight className="h-4 w-4" /></Button></Link>
              <Link to="/patient"><Button size="lg" variant="outline" data-testid="hero-patient-portal" className="border-slate-300">Patient portal</Button></Link>
            </div>
            <a href={PLAY_URL} target="_blank" rel="noreferrer" data-testid="hero-google-play" className="mt-5 inline-block transition-transform hover:-translate-y-0.5">
              <img src="/google-play-badge.png" alt="Get it on Google Play" className="h-12 w-auto" />
            </a>
            <div className="mt-8 flex items-center gap-2 text-sm text-slate-500">
              <Globe className="h-4 w-4" />
              {JURISDICTIONS.map((j) => <span key={j.code} title={j.name} className="text-lg">{j.flag}</span>)}
              <span className="ml-1">Multi-jurisdiction ready</span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.1 }} className="relative">
            <div className="overflow-hidden rounded-2xl border border-slate-200 shadow-2xl">
              <img src="https://images.unsplash.com/photo-1758691462878-6edc3d3da1be?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Clinician" className="h-full w-full object-cover" />
            </div>
          </motion.div>
        </div>
      </section>

      <section className="border-t border-slate-100 bg-slate-50 py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Everything your facility runs on</h2>
          <p className="mt-2 max-w-2xl text-slate-600">One connected platform, matching the FHAPAS mobile experience — now installable on any browser.</p>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f, i) => (
              <motion.div key={f.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-teal-50 text-teal-700"><f.icon className="h-5 w-5" /></div>
                <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{f.title}</h3>
                <p className="mt-1.5 text-sm text-slate-600">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white py-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-4 text-sm text-slate-500 sm:flex-row sm:px-6">
          <div className="flex items-center gap-2">
            <img src="/icon-192.png" alt="FHAPAS" className="h-6 w-6 rounded" />
            <span>© {new Date().getFullYear()} Fendana Health. All rights reserved.</span>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <a href={PLAY_URL} target="_blank" rel="noreferrer" data-testid="footer-google-play"><img src="/google-play-badge.png" alt="Get it on Google Play" className="h-9 w-auto" /></a>
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-teal-700">Privacy</Link>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-teal-700">Terms</Link>
            <Link to="/data-deletion" data-testid="footer-data-deletion" className="hover:text-teal-700">Data deletion</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
